import type { WSBotMessage } from '@/types/WSBotMessage'
import { createIsTypingCommand, type Commands } from './commandBuilder'
import { createOptimisticUpdateUserMessage, type ConversationMessages, type UserMessage, createMessageKey } from './messageBuilder'
import { parseMessage } from './parser'
import { getJWTToken } from './token'
import { ref } from 'vue'
import type { Operator } from '@/types'

const processedUUIDs = new Set()
const conversation = ref(new Map())
const optimisticUpdates = new Map()
// initWithHistory()


// If any open widget has sent a message we get an uuid back
// we can check if
//  another tab send something
//  a message we send has aririved and is currently processed
const handleLoopback = (uuid: string, tempId: string) => {
  processedUUIDs.add(uuid)
  optimisticUpdates.delete(tempId)
}

const addOptimisticUpdate = ({ text, tempId, timestamp }: { text: string, tempId: string, timestamp?: number }) => {
  const cnvmsg = createOptimisticUpdateUserMessage(text, tempId, timestamp);
  optimisticUpdates.set(tempId, cnvmsg)
  conversation.value.set(tempId, cnvmsg)
  // console.log('add ou', text, tempId, optimisticUpdates)
}

const resolveOptimisticUpdate = (messages: UserMessage[]) => {
  messages.forEach((item) => {
    const possibleMatches = [...optimisticUpdates].filter(([, v]) => {
      const exactMessageMatch = v.data.text === item.data.text;
      const isAnonymizedMessage = item.messageType === 'UserMessage' && createMessageKey(item.timestamp, item.source, 'CoversationMessage') === v.key;
      return exactMessageMatch || isAnonymizedMessage;
    });

    if (possibleMatches.length === 1) {
      console.log('we got a match', possibleMatches[0][0], item.key)
      optimisticUpdates.delete(possibleMatches[0][0])
      processedUUIDs.add(item.key)
    }
  })
}

const isTypingOffCmd = (msg: Commands | ConversationMessages) => msg.type === 'cmd' && msg.cmd === 'typing' && msg.value === false

type AllMessagesAndCommands = Array<Commands | ConversationMessages>

const updateConversation = (messages: AllMessagesAndCommands) => {
  if (!messages) return
  if (messages.length === 0) return

  // check if we have any unhandled commands or messages
  const preOptimisticUpdateCheck = messages.filter(
    (msg) => (!processedUUIDs.has(msg.key) || isTypingOffCmd(msg)) && !optimisticUpdates.has(msg.key)
  )
  if (preOptimisticUpdateCheck.length === 0) return
  // console.log('preOptimisticUpdateCheck', preOptimisticUpdateCheck)

  const userMessages = preOptimisticUpdateCheck
    .filter((item): item is UserMessage => {
      return item.type === 'msg' && 'messageType' in item && item.messageType === 'UserMessage'
    })

  if (userMessages.length > 0) {
    resolveOptimisticUpdate(userMessages)
  }

  const unhandled = messages.filter(
    // ! isTyping commands may be generated with the same 'time', because it is generated for the same message (backend)
    (msg) => (!processedUUIDs.has(msg.key) || isTypingOffCmd(msg)) && !processedUUIDs.has(msg.uuid)
  )
  // console.log(optimisticUpdates)
  // console.log('unhandled', unhandled)
  const cmds = unhandled.filter((item): item is Commands => item.type === 'cmd')
  const msgs = unhandled.filter((item): item is ConversationMessages => item.type === 'msg')

  cmds.forEach((message) => {
    processedUUIDs.add(message.key)
  })
  msgs.forEach((message) => {
    conversation.value.set(message.key, message)
    processedUUIDs.add(message.key)
  })

  // we don't get a dedicated isTyping command after a message
  // we implicit inject one after we got a message
  if (msgs.length > 0) {
    cmds.push(createIsTypingCommand(false))
  }
  processCommands(cmds)
  // console.log(conversation.value)
}

type MetaFields = {
  uuid?: string
  messageFrom?: string
  isFromClient?: boolean
}
const isEmptyMessage = (item: WSBotMessage, meta: MetaFields) => meta.isFromClient && !item.message
const processMessage = (message: WSBotMessage, operator: Operator, expandLinks: boolean, metafields: MetaFields = {}) => {
  if (isEmptyMessage(message, metafields)) {
    //logger.warn('Empty message received', message)
    return
  }

  const jwtToken = getJWTToken();
  if (!jwtToken) {
    console.error('No JWT Token found, cannot process message', message)
    return
  }
  if (!metafields.uuid) {
    // we don't have a uuid
    // but we need to process the message
    // for typing indicators and other stuff
    // console.error('No UUID found', message)
  }

  const parsedMessage = parseMessage(message, {
    jwtToken: jwtToken,
    operator: operator,
    expandBotMessageLinks: expandLinks,
    uuid: metafields.uuid,
    messageFrom: metafields.messageFrom
  })
  if (!parsedMessage) return
  updateConversation(parsedMessage)
}

let processCommands = (commands: Commands[]) => {
  console.warn('No command processor set', commands)
}

export function useConversation(commandHandler: (commands: Commands[]) => void) {
  processCommands = commandHandler

  return {
    conversation,
    optimisticUpdates,
    processMessage,
    addOptimisticUpdate,
    handleLoopback
  }
}
